import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Platform,
} from 'react-native';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { db } from '../../config/firebase';
import { useAdminThemeContext } from '../../contexts/AdminThemeContext';
import { AdminSectionHeader } from './AdminSectionHeader';
import { DEFAULT_INTERESTS } from '../../constants/defaultInterests';
import {
  aggregateCommunityInterests,
  normalizeInterestItems,
  uniqueInterestId,
} from '../../utils/interestUtils';

const emptyForm = { label: '', emoji: '' };
const GRID_GAP = 10;
const COLUMN_MIN = 220;

function SortableInterestCard({
  item,
  index,
  editingId,
  editDraft,
  setEditDraft,
  saving,
  colors,
  adminStyles,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  onDelete,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const cardStyle = {
    backgroundColor: colors.cardBg,
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: isDragging ? colors.navActive : colors.cardBorder,
    width: '100%',
    opacity: isDragging ? 0.92 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
    boxShadow: isDragging ? `0 8px 20px ${colors.shadowColor}` : 'none',
  };

  return (
    <View ref={setNodeRef} style={cardStyle}>
      {editingId === item.id ? (
        <>
          <Text style={{ color: colors.textMuted, fontSize: 11, marginBottom: 8 }}>ID: {item.id}</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <View style={{ flex: 1, minWidth: 120 }}>
              <TextInput
                value={editDraft.label}
                onChangeText={(label) => setEditDraft((prev) => ({ ...prev, label }))}
                style={[adminStyles.formInput, { marginBottom: 0 }]}
                placeholderTextColor={colors.inputPlaceholder}
              />
            </View>
            <View style={{ width: 56 }}>
              <TextInput
                value={editDraft.emoji}
                onChangeText={(emoji) => setEditDraft((prev) => ({ ...prev, emoji }))}
                style={[adminStyles.formInput, { marginBottom: 0, textAlign: 'center' }]}
                placeholderTextColor={colors.inputPlaceholder}
              />
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity
              onPress={onSaveEdit}
              style={{ paddingVertical: 8, paddingHorizontal: 12, backgroundColor: colors.navActive, borderRadius: 8 }}
            >
              <Text style={{ color: colors.textOnAccent, fontWeight: '700', fontSize: 13 }}>Save</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={onCancelEdit}
              style={{ paddingVertical: 8, paddingHorizontal: 12, backgroundColor: colors.cardBgSubtle, borderRadius: 8, borderWidth: 1, borderColor: colors.cardBorder }}
            >
              <Text style={{ color: colors.textPrimary, fontWeight: '700', fontSize: 13 }}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </>
      ) : (
        <>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
            <TouchableOpacity
              {...listeners}
              {...attributes}
              style={{
                paddingVertical: 4,
                paddingHorizontal: 6,
                borderRadius: 6,
                backgroundColor: colors.cardBgSubtle,
                borderWidth: 1,
                borderColor: colors.cardBorder,
                cursor: Platform.OS === 'web' ? 'grab' : undefined,
              }}
              accessibilityLabel={`Drag to reorder ${item.label}`}
            >
              <Text style={{ color: colors.textMuted, fontWeight: '700', fontSize: 14 }}>⠿</Text>
            </TouchableOpacity>
            {item.emoji ? <Text style={{ fontSize: 20 }}>{item.emoji}</Text> : null}
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ color: colors.textPrimary, fontWeight: '700', fontSize: 15 }} numberOfLines={2}>
                {item.label}
              </Text>
              <Text style={{ color: colors.textMuted, fontSize: 10, marginTop: 2 }} numberOfLines={1}>
                {item.id}
              </Text>
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: 6, marginTop: 10 }}>
            <TouchableOpacity
              onPress={() => onStartEdit(item)}
              style={{ flex: 1, paddingVertical: 7, backgroundColor: colors.cardBgSubtle, borderRadius: 8, borderWidth: 1, borderColor: colors.cardBorder, alignItems: 'center' }}
            >
              <Text style={{ color: colors.textPrimary, fontWeight: '700', fontSize: 12 }}>Edit</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => onDelete(item.id)}
              style={{ flex: 1, paddingVertical: 7, backgroundColor: '#c0392b', borderRadius: 8, alignItems: 'center' }}
            >
              <Text style={{ color: '#fff', fontWeight: '700', fontSize: 12 }}>Delete</Text>
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  );
}

export function AdminInterests() {
  const { styles: adminStyles, colors } = useAdminThemeContext();
  const [catalogItems, setCatalogItems] = useState(DEFAULT_INTERESTS);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [fromFirestore, setFromFirestore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyForm);
  const [newInterest, setNewInterest] = useState(emptyForm);
  const [usersLoading, setUsersLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  useEffect(() => {
    const ref = doc(db, 'system', 'interests');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const items = normalizeInterestItems(snap.data()?.items);
        if (items.length > 0) {
          setCatalogItems(items);
          setFromFirestore(true);
        } else {
          setCatalogItems(DEFAULT_INTERESTS);
          setFromFirestore(false);
        }
        setCatalogLoading(false);
      },
      (err) => {
        console.error('system/interests', err);
        setCatalogLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        if (cancelled) return;
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('load users for interests', err);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const communityInterests = useMemo(
    () => aggregateCommunityInterests(users, catalogItems),
    [users, catalogItems]
  );

  const persistCatalog = async (items) => {
    const auth = getAuth();
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'system', 'interests'),
        {
          items: normalizeInterestItems(items),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || null,
        },
        { merge: true }
      );
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to save interests');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({ label: item.label, emoji: item.emoji || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(emptyForm);
  };

  const saveEdit = async () => {
    const label = editDraft.label.trim();
    if (!label || !editingId) return;
    const next = catalogItems.map((item) =>
      item.id === editingId
        ? { ...item, label, emoji: editDraft.emoji.trim() }
        : item
    );
    await persistCatalog(next);
    cancelEdit();
  };

  const deleteInterest = async (id) => {
    if (!confirm('Remove this interest from the default list? Users who already selected it will keep their value.')) {
      return;
    }
    await persistCatalog(catalogItems.filter((item) => item.id !== id));
    if (editingId === id) cancelEdit();
  };

  const addInterest = async () => {
    const label = newInterest.label.trim();
    if (!label) return;
    const id = uniqueInterestId(
      label,
      catalogItems.map((item) => item.id)
    );
    await persistCatalog([
      ...catalogItems,
      { id, label, emoji: newInterest.emoji.trim() },
    ]);
    setNewInterest(emptyForm);
  };

  const promoteCommunityInterest = async (label, emoji = '') => {
    const trimmed = String(label || '').trim();
    if (!trimmed) return;
    const existing = catalogItems.find(
      (item) => item.label.toLowerCase() === trimmed.toLowerCase() || item.id === trimmed
    );
    if (existing) {
      alert(`"${existing.label}" is already in the default list.`);
      return;
    }
    const id = uniqueInterestId(
      trimmed,
      catalogItems.map((item) => item.id)
    );
    await persistCatalog([...catalogItems, { id, label: trimmed, emoji: emoji.trim() }]);
  };

  const publishBuiltInDefaults = async () => {
    if (
      !confirm(
        'Save the built-in default interests to Firestore? This becomes the live list shown on registration and profile.'
      )
    ) {
      return;
    }
    await persistCatalog(DEFAULT_INTERESTS);
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = catalogItems.findIndex((item) => item.id === active.id);
    const newIndex = catalogItems.findIndex((item) => item.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    await persistCatalog(arrayMove(catalogItems, oldIndex, newIndex));
  };

  const panelStyle = {
    backgroundColor: colors.cardBg,
    borderRadius: 10,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${COLUMN_MIN}px, 1fr))`,
    gap: GRID_GAP,
    width: '100%',
  };

  return (
    <View style={{ width: '100%', maxWidth: 1200, paddingBottom: 40 }}>
      <AdminSectionHeader
        title="Default interests"
        subtitle="Drag to reorder. These chips appear on registration and profile and sync site-wide via Firestore."
        isFirst
      />

      <Text style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 12, lineHeight: 20 }}>
        Source: {fromFirestore ? 'Firestore (system/interests)' : 'Built-in defaults (not yet saved to Firestore)'}
        {saving ? ' · Saving…' : ''}
      </Text>

      {!fromFirestore && (
        <TouchableOpacity
          onPress={publishBuiltInDefaults}
          disabled={saving}
          style={{
            alignSelf: 'flex-start',
            paddingVertical: 10,
            paddingHorizontal: 16,
            backgroundColor: colors.navActive,
            borderRadius: 8,
            marginBottom: 16,
            opacity: saving ? 0.6 : 1,
          }}
        >
          <Text style={{ color: colors.textOnAccent, fontWeight: '700' }}>Publish built-in defaults</Text>
        </TouchableOpacity>
      )}

      <View style={panelStyle}>
        <Text style={[adminStyles.cardTitle, { marginBottom: 12 }]}>Add interest</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <View style={{ flex: 1, minWidth: 140 }}>
            <Text style={{ color: colors.textSecondary, fontSize: 12, marginBottom: 4 }}>Label</Text>
            <TextInput
              value={newInterest.label}
              onChangeText={(label) => setNewInterest((prev) => ({ ...prev, label }))}
              placeholder="e.g. Photography"
              placeholderTextColor={colors.inputPlaceholder}
              style={[adminStyles.formInput, { marginBottom: 0 }]}
            />
          </View>
          <View style={{ width: 72 }}>
            <Text style={{ color: colors.textSecondary, fontSize: 12, marginBottom: 4 }}>Emoji</Text>
            <TextInput
              value={newInterest.emoji}
              onChangeText={(emoji) => setNewInterest((prev) => ({ ...prev, emoji }))}
              placeholder="📷"
              placeholderTextColor={colors.inputPlaceholder}
              style={[adminStyles.formInput, { marginBottom: 0, textAlign: 'center' }]}
            />
          </View>
          <TouchableOpacity
            onPress={addInterest}
            disabled={!newInterest.label.trim() || saving}
            style={{
              paddingVertical: 12,
              paddingHorizontal: 18,
              backgroundColor: colors.navActive,
              borderRadius: 8,
              opacity: !newInterest.label.trim() || saving ? 0.5 : 1,
            }}
          >
            <Text style={{ color: colors.textOnAccent, fontWeight: '700' }}>Add</Text>
          </TouchableOpacity>
        </View>
      </View>

      {catalogLoading ? (
        <ActivityIndicator size="large" color={colors.accentGreen} />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={catalogItems.map((item) => item.id)} strategy={rectSortingStrategy}>
            <View style={gridStyle}>
              {catalogItems.map((item, index) => (
                <SortableInterestCard
                  key={item.id}
                  item={item}
                  index={index}
                  editingId={editingId}
                  editDraft={editDraft}
                  setEditDraft={setEditDraft}
                  saving={saving}
                  colors={colors}
                  adminStyles={adminStyles}
                  onSaveEdit={saveEdit}
                  onCancelEdit={cancelEdit}
                  onStartEdit={startEdit}
                  onDelete={deleteInterest}
                />
              ))}
            </View>
          </SortableContext>
        </DndContext>
      )}

      <AdminSectionHeader
        title="Community-added interests"
        subtitle="Custom interests users typed on registration or profile. Promote any to add them to the default chip list."
      />

      {usersLoading ? (
        <ActivityIndicator size="large" color={colors.accentGreen} />
      ) : communityInterests.length === 0 ? (
        <Text style={{ color: colors.textSecondary, fontSize: 14 }}>
          No custom interests found yet — users can add their own from the profile or registration form.
        </Text>
      ) : (
        <View style={gridStyle}>
          {communityInterests.map((row) => (
            <View
              key={row.label.toLowerCase()}
              style={{
                backgroundColor: colors.cardBg,
                borderRadius: 10,
                padding: 12,
                borderWidth: 1,
                borderColor: colors.cardBorder,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <Text style={{ color: colors.textPrimary, fontWeight: '700', fontSize: 15, flex: 1 }}>
                  {row.label}
                </Text>
                <Text
                  style={{
                    color: colors.textOnAccent,
                    fontSize: 11,
                    fontWeight: '700',
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: 6,
                    backgroundColor: colors.statAccent,
                  }}
                >
                  {row.count} user{row.count === 1 ? '' : 's'}
                </Text>
              </View>
              {row.sampleUsers.length > 0 && (
                <Text style={{ color: colors.textMuted, fontSize: 12, marginTop: 6 }}>
                  e.g. {row.sampleUsers.join(', ')}
                </Text>
              )}
              <TouchableOpacity
                onPress={() => promoteCommunityInterest(row.label)}
                disabled={saving}
                style={{
                  alignSelf: 'flex-start',
                  marginTop: 10,
                  paddingVertical: 8,
                  paddingHorizontal: 14,
                  backgroundColor: colors.navActive,
                  borderRadius: 8,
                  opacity: saving ? 0.6 : 1,
                }}
              >
                <Text style={{ color: colors.textOnAccent, fontWeight: '700', fontSize: 13 }}>Add as default</Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}
